/**
 * What a vendor may see of one event in the vendor portal.
 *
 * A vendor reaches an event only through the office's link — the EVENT→VENDOR
 * `entity_links` row the Vendors tab adds — and, being on it, sees **nothing**
 * until the office grants something. The grants live beside the messaging
 * grants (`messagingPermissions.ts`) in that link's `metadata`, so they are per
 * event and per vendor business: every login of the vendor holds the same.
 *
 * | grant | metadata key | what it opens |
 * |---|---|---|
 * | their expenses | `portalExpenses` | the vendor's own expense lines, and uploading a quote or invoice to one |
 * | their items | `portalItems` | the itemized list of the vendor's own booked lines — every item's details — and the files on those items and lines, read only |
 * | their timeline | `portalTimeline` | the timeline entries assigned to the vendor |
 * | a folder | `portalFolders` | that folder of the event's files and everything under it — `read` (view, download) or `upload` (also upload, add folders, and change or delete what they themselves put there) |
 * | a floor plan | `portalFloorPlans` | that plan, read only; guest names at the tables only with `guestNames` |
 * | adding tasks | `portalAddTasks` | adding tasks of their own to the event — the tasks assigned to them they see and tick without it |
 *
 * ⚠️ **Only a literal `true` grants, and only a well-formed entry counts.** The
 * column is free-form JSON written by more than one screen; a string "false",
 * a missing key or a folder entry with no node must never read as permission.
 */
export const VENDOR_FOLDER_ACCESS = ['read', 'upload'];
/** The metadata keys the grants are stored under — one each, so a patch changes one. */
export const VENDOR_PORTAL_KEYS = {
    expenses: 'portalExpenses',
    timeline: 'portalTimeline',
    items: 'portalItems',
    folders: 'portalFolders',
    floorPlans: 'portalFloorPlans',
    addTasks: 'portalAddTasks',
};
/** A file-library node a folder grant may name: an event's library, or a folder in one. */
export const VENDOR_FOLDER_NODE_PATTERN = /^(event|folder):[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export const NO_VENDOR_PORTAL_GRANTS = {
    canSeeExpenses: false,
    canSeeTimeline: false,
    canSeeItems: false,
    folders: [],
    floorPlans: [],
    canAddTasks: false,
};
function record(value) {
    return value && typeof value === 'object' && !Array.isArray(value)
        ? value
        : {};
}
function readFolders(value) {
    if (!Array.isArray(value))
        return [];
    const byNode = new Map();
    for (const raw of value) {
        const entry = record(raw);
        const nodeId = entry.nodeId;
        const access = entry.access;
        if (typeof nodeId !== 'string' || !VENDOR_FOLDER_NODE_PATTERN.test(nodeId))
            continue;
        if (access !== 'read' && access !== 'upload')
            continue;
        const held = byNode.get(nodeId);
        // The same folder twice: the wider access stands.
        if (!held || (held.access === 'read' && access === 'upload')) {
            byNode.set(nodeId, { nodeId, access });
        }
    }
    return [...byNode.values()];
}
function readFloorPlans(value) {
    if (!Array.isArray(value))
        return [];
    const byPlan = new Map();
    for (const raw of value) {
        const entry = record(raw);
        const floorPlanId = entry.floorPlanId;
        if (typeof floorPlanId !== 'string' || floorPlanId === '')
            continue;
        const guestNames = entry.guestNames === true || byPlan.get(floorPlanId)?.guestNames === true;
        byPlan.set(floorPlanId, { floorPlanId, guestNames });
    }
    return [...byPlan.values()];
}
/** A vendor's portal grants on one event, from its event link's `metadata`. */
export function vendorPortalOf(metadata) {
    const meta = record(metadata);
    return {
        canSeeExpenses: meta[VENDOR_PORTAL_KEYS.expenses] === true,
        canSeeTimeline: meta[VENDOR_PORTAL_KEYS.timeline] === true,
        canSeeItems: meta[VENDOR_PORTAL_KEYS.items] === true,
        folders: readFolders(meta[VENDOR_PORTAL_KEYS.folders]),
        floorPlans: readFloorPlans(meta[VENDOR_PORTAL_KEYS.floorPlans]),
        canAddTasks: meta[VENDOR_PORTAL_KEYS.addTasks] === true,
    };
}
/**
 * What a person holds on an event through every vendor they work for that is
 * linked to it — each link a separate trust the office extended, so the union.
 */
export function mergeVendorPortalGrants(all) {
    return {
        canSeeExpenses: all.some((g) => g.canSeeExpenses),
        canSeeTimeline: all.some((g) => g.canSeeTimeline),
        canSeeItems: all.some((g) => g.canSeeItems),
        folders: readFolders(all.flatMap((g) => g.folders)),
        floorPlans: readFloorPlans(all.flatMap((g) => g.floorPlans)),
        canAddTasks: all.some((g) => g.canAddTasks),
    };
}
/** Whether the vendor has been given anything to see on the event at all. */
export function hasAnyVendorPortalGrant(grants) {
    return (grants.canSeeExpenses ||
        grants.canSeeTimeline ||
        grants.canSeeItems ||
        grants.folders.length > 0 ||
        grants.floorPlans.length > 0 ||
        grants.canAddTasks);
}
/** The folder grant a node id names, if the vendor holds one on it directly. */
export function folderGrantFor(grants, nodeId) {
    return grants.folders.find((grant) => grant.nodeId === nodeId);
}
//# sourceMappingURL=vendorPortal.js.map